import React, { useContext, useEffect, useState } from "react"
import Button from "../../ui/Button"
import { useForm } from "react-hook-form"
import { AdminContext } from "~/contexts/AdminContext"
import _ from "lodash"
import api from "../../../api2"
import { CampProps, CampingStyleProps } from "../../../interfaces"
import Spinner from "../../ui/Spinner"
import Alert from "../../ui/Alert"
import api2 from "../../../api2"
import { IAutomatedMessages } from "~/interfaces/AutomatedMessages"
import { useDebouncedCallback } from "use-debounce"
import SettingsNavBar from "./components/SettingsNavBar"
import { FormValues } from "~/components/Admin/Settings/PricesForm/types"
import { mapPriceConfigToData } from "~/components/Admin/Settings/PricesForm/utils"
import isEmailValidAndSingle, { emailValidationResponse } from "~/utils/isEmailValidAndSingle"

enum tabs {
  camp = "camp",
  policyAndRules = "policy-and-rules",
  pricing = "pricing",
  products = "products",
  invoices = "invoices",
  coupons = "coupons",
  automatedMessages = "automated-messages",
  users = "users",
}

const CHECKIN_AUTOMATED_MESSAGES = "checkin"
const CHECKOUT_AUTOMATED_MESSAGES = "checkout"

const parseSeasonDates = (date?: string) => {
  if (!date) return null
  const parts = date.split("-")
  if (parts.length !== 2) return null

  const parsed = parts.map((num) => {
    const n = parseInt(num, 10)
    return isNaN(n) ? null : String(n)
  })

  return parsed.includes(null) ? null : parsed
}

const formSanitized = (
  camp: CampProps,
  campingStyles: CampingStyleProps[],
  automatedMessage: Record<typeof CHECKIN_AUTOMATED_MESSAGES | typeof CHECKOUT_AUTOMATED_MESSAGES, IAutomatedMessages>,
): FormValues => {
  const season_start_num_values = parseSeasonDates(camp.season_start_mm_dd)
  const season_end_num_values = parseSeasonDates(camp.season_end_mm_dd)
  const booking_start_num_values = parseSeasonDates(camp.booking_start_mm_dd)

  return {
    before_checkin_subject: automatedMessage?.checkin?.subject,
    before_checkin_content: automatedMessage?.checkin?.body,
    minutes_before_checkin:
      typeof automatedMessage?.checkin?.minutes_before_checkin === "number"
        ? automatedMessage?.checkin?.minutes_before_checkin
        : null,
    before_checkin_cc_camp_notification_emails: automatedMessage?.checkin?.is_copying_camp_notification_emails,
    after_checkout_subject: automatedMessage?.checkout?.subject,
    after_checkout_content: automatedMessage?.checkout?.body,
    minutes_after_checkout:
      typeof automatedMessage?.checkout?.minutes_after_checkout === "number"
        ? automatedMessage?.checkout?.minutes_after_checkout
        : null,
    after_checkout_cc_camp_notification_emails: automatedMessage?.checkout?.is_copying_camp_notification_emails,
    ...camp,
    season_start_mm: season_start_num_values?.[0] || null,
    season_start_dd: season_start_num_values?.[1] || null,
    season_end_mm: season_end_num_values?.[0] || null,
    season_end_dd: season_end_num_values?.[1] || null,
    booking_start_mm: booking_start_num_values?.[0] || null,
    booking_start_dd: booking_start_num_values?.[1] || null,
    updated_at: null,
    created_at: null,
    camping_styles: campingStyles.map((cs) => ({ id: cs.id, sites: [] })) as CampingStyleProps[],
    discounts: null,
    camp_discounts: null,
    prerequisites: null,
    checkin_policy: campingStyles.find((style) => style.checkin_policy)?.checkin_policy,
    refund_policy: campingStyles.find((style) => style.refund_policy)?.refund_policy,
  }
}

export type NotificationType = "succes" | "error"

const Settings = () => {
  const { camp, setCamp, reloadAccessibleCamps, campingStyles, setCampingStyles, showNotification } =
    useContext(AdminContext)
  const [selectedTab, setSelectedTab] = useState<string>(tabs.camp)
  const [automatedMessage, setAutomatedMessage] = useState<Record<"checkin" | "checkout", IAutomatedMessages>>(null)
  const [defaultValues, setDefaultValues] = useState<FormValues>(formSanitized(camp, campingStyles, automatedMessage))
  const [isLoading, setIsLoading] = useState(false)
  const [messagesLoading, setMessagesLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string>(null)

  const reactHookForm = useForm({ defaultValues })
  const {
    reset,
    watch,
    setError,
    clearErrors,
    formState: { isDirty },
  } = reactHookForm
  const currentValues = watch()

  useEffect(() => {
    setIsLoading(true)
    setDefaultValues(formSanitized(camp, campingStyles, automatedMessage))
    reset(formSanitized(camp, campingStyles, automatedMessage))
    setIsLoading(false)
  }, [camp, automatedMessage])

  useEffect(() => {
    if (window.location.pathname === "/admin/settings") {
      localStorage.setItem("from", window.location.pathname)
      localStorage.setItem("toTab", "camp")
      localStorage.setItem("toNav", "settings")

      window.location.href = "/admin/reservations"
    }
  }, [])

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ""
        // TODO: make this app-native
        alert("You have unsaved changes. Are you sure you want to leave?")
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [isDirty])

  const handleCancel = () => {
    reset(defaultValues)
    setErrorMessage(null)
  }

  const validateMessageFields = () => {
    if (typeof currentValues.minutes_before_checkin === "number") {
      if (!currentValues.before_checkin_subject?.length) {
        setErrorMessage("Before check-in email subject can't be blank")
        return true
      }
      if (!currentValues.before_checkin_content?.length) {
        setErrorMessage("Before check-in email content can't be blank")
        return true
      }
    }

    if (typeof currentValues.minutes_after_checkout === "number") {
      if (!currentValues.after_checkout_subject?.length) {
        setErrorMessage("After check-out email subject can't be blank")
        return true
      }
      if (!currentValues.after_checkout_content?.length) {
        setErrorMessage("After check-out email content can't be blank")
        return true
      }
    }

    return false
  }

  const validateCampFields = (): { field: string; error: string } | true => {
    const emailValidation = isEmailValidAndSingle(currentValues.email)

    if (emailValidation === emailValidationResponse.INCORRECT) {
      return { field: "email", error: "Invalid email format" }
    } else if (emailValidation === emailValidationResponse.NOT_SINGLE) {
      return { field: "email", error: "More than one email present" }
    }

    return true
  }

  const formatDate = (month: string | null, day: string | null) => {
    if (!day?.length || !month?.length) {
      return null
    }

    const formattedDay = day.padStart(2, "0")
    const formattedMonth = month.padStart(2, "0")
    return `${formattedMonth}-${formattedDay}`
  }

  const handleSave = async () => {
    setIsLoading(true)
    setErrorMessage(null)
    clearErrors()

    const campValidation = validateCampFields()

    if (campValidation?.error) {
      setError(campValidation.field, campValidation.error)
      setErrorMessage(
        `${campValidation.field.charAt(0).toUpperCase() + campValidation.field.slice(1)}: ${campValidation.error}`,
      )
      setIsLoading(false)
      return
    }

    if (currentValues.priceConfig && selectedTab.includes(tabs.pricing)) {
      let pricesPromises = []
      const currentCampingStyleId = selectedTab.split("_")[1]
      pricesPromises.push(
        api.CampingStyles.upsertPricesFor(currentCampingStyleId, mapPriceConfigToData(currentValues.priceConfig)),
      )

      await Promise.all(pricesPromises)
        .then(
          (response) =>
            response.length > 0 &&
            showNotification({
              title: "Saved new prices sucessfully",
              type: "success",
            }),
        )
        .catch((err: any) => {
          if (err.errors) {
            Object.keys(err.errors).forEach((key: any, i: number) => {
              if (i === 0) {
                setErrorMessage(`${key}.${err.errors[key]}`)
              }

              setError(key, { message: err.errors[key] })
            })
          }
        })
        .finally(() => setIsLoading(false))
    }

    const isCheckinFilled = currentValues.before_checkin_subject?.length && currentValues.before_checkin_content?.length
    const isCheckoutFilled =
      currentValues.after_checkout_subject?.length && currentValues.after_checkout_content?.length

    if (validateMessageFields()) {
      setIsLoading(false)
      return
    }

    const automatedMessages: Record<"checkin" | "checkout", IAutomatedMessages> = {
      checkin: isCheckinFilled && {
        ...automatedMessage?.checkin,
        name: "checkin",
        subject: currentValues.before_checkin_subject || "",
        body: currentValues.before_checkin_content || "",
        minutes_before_checkin: currentValues.minutes_before_checkin,
        is_copying_camp_notification_emails: currentValues.before_checkin_cc_camp_notification_emails,
      },
      checkout: isCheckoutFilled && {
        ...automatedMessage?.checkout,
        name: "checkout",
        subject: currentValues.after_checkout_subject || "",
        body: currentValues.after_checkout_content || "",
        minutes_after_checkout: currentValues.minutes_after_checkout,
        is_copying_camp_notification_emails: currentValues.after_checkout_cc_camp_notification_emails,
      },
    }

    const seasonDates = {
      season_start_mm_dd: formatDate(currentValues.season_start_mm, currentValues.season_start_dd),
      season_end_mm_dd: formatDate(currentValues.season_end_mm, currentValues.season_end_dd),
      booking_start_mm_dd: formatDate(currentValues.booking_start_mm, currentValues.booking_start_dd),
    }

    const promises = [api.Camps.update(camp.id, { ...currentValues, ...seasonDates })]

    if (!_.isEqual(automatedMessages, automatedMessage)) {
      promises.push(
        api2.AutomatedMessage.update(
          camp.id,
          [automatedMessages.checkin, automatedMessages.checkout].filter((item) => item),
        ),
      )
    }

    if (currentValues.priceConfig) {
      console.log("currentValues.priceConfig", currentValues.priceConfig)
    }

    return Promise.all(promises)
      .then(([campResponse, messagesResponse]) => {
        const { camp: c, camping_styles: cs } = campResponse as { camp: CampProps; camping_styles: CampingStyleProps[] }

        let checkin, checkout

        if (messagesResponse) {
          checkin = messagesResponse.automated_message_templates?.filter(
            (message) => message.name.toLowerCase() === CHECKIN_AUTOMATED_MESSAGES,
          )[0]
          checkout = messagesResponse.automated_message_templates?.filter(
            (message) => message.name.toLowerCase() === CHECKOUT_AUTOMATED_MESSAGES,
          )[0]

          setAutomatedMessage({
            checkin,
            checkout,
          })
        }

        const sanitizedData = formSanitized(c, cs, { checkin, checkout })

        setDefaultValues(sanitizedData)
        reset(sanitizedData)
        setCamp((prev) => ({ ...prev, ...c }))
        setCampingStyles((prev) => {
          const updatedStyles = prev.map((style) => {
            const updatedStyle = cs.find((updated) => updated.id === style.id)
            return updatedStyle ? { ...style, ...updatedStyle } : style
          })
          const newStyles = cs.filter((updated) => !prev.some((style) => style.id === updated.id))
          return [...updatedStyles, ...newStyles]
        })
        reloadAccessibleCamps()
        setIsLoading(false)
        showNotification({
          type: "success",
          title: "Changes successfully saved!",
          dismissAfter: 5000,
        })
      })
      .catch((err) => {
        console.error(err)
        setErrorMessage(err.message)

        if (err.errors) {
          Object.keys(err.errors).forEach((key: keyof CampProps, i: number) => {
            if (i === 0) {
              setErrorMessage(`${key}.${err.errors[key]}`)
            }

            setError(key, { message: err.errors[key] })
          })
        }

        setIsLoading(false)
        showNotification({
          type: "error",
          title: "Changes not saved",
          description: err.message,
          dismissAfter: 5000,
        })
      })
  }

  const fetchAutomatedMessages = useDebouncedCallback(() => {
    setMessagesLoading(true)
    api2.AutomatedMessage.fetch(camp.id)
      .then((res) => {
        const checkin = res.automated_message_templates?.filter(
          (message) => message.name.toLowerCase() === CHECKIN_AUTOMATED_MESSAGES,
        )[0]
        const checkout = res.automated_message_templates?.filter(
          (message) => message.name.toLowerCase() === CHECKOUT_AUTOMATED_MESSAGES,
        )[0]

        setAutomatedMessage({
          checkin,
          checkout,
        })
      })
      .finally(() => {
        setMessagesLoading(false)
      })
  }, 100)

  useEffect(() => {
    fetchAutomatedMessages()
  }, [camp])

  return (
    <div className="w-full md:flex md:flex-row space-x-1.5">
      <SettingsNavBar onTabChange={setSelectedTab} />
      <div className="w-full justify-center">
        <div className="bg-white top-20 mx-4 py-4 z-10">
          {selectedTab !== "products" && (
            <div className="flex w-full items-center">
              {isDirty && <p className="text-gray-400">There are some changes that should be saved</p>}
              <Button disabled={!isDirty || isLoading} className="mr-4 ml-auto" onClick={handleCancel}>
                Cancel
              </Button>
              <Button disabled={!isDirty || isLoading} variant="green" onClick={handleSave}>
                {isLoading ? <Spinner size={4} /> : "Save"}
              </Button>
            </div>
          )}
        </div>
        <div className="mx-4 mb-48">
          {errorMessage && <Alert description={errorMessage} />}
        </div>
      </div>
    </div>
  )
}

export default Settings
